import { lazy, Suspense, useEffect } from 'react'
import { useAuth } from '@clerk/react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { TopBar } from './components/ui/TopBar'
import { BottomNav } from './components/ui/BottomNav'
import { ThemePreviewBar } from './components/ui/ThemePreviewBar'
import { ChromeProvider } from './components/chrome/ChromeContext'
import { TermsAcceptanceModal } from './components/ui/TermsAcceptanceModal'
import { OfflineBanner } from './components/ui/OfflineBanner'
import { HelpVideoSheet } from './components/help/HelpVideoSheet'
import { UpdateToast } from './components/ui/UpdateToast'
import { useDocumentTitle } from './hooks/useDocumentTitle'
import { setAuthTokenGetter } from './services/api'
import { useFlagStore } from './hooks/useFeatureFlags'
import {
  applyAccent,
  applyTheme,
  clearAccentVarsFromDom,
  deriveEffectiveTheme,
  getActiveForcedTheme,
  isPaidProfile,
  shouldApplyAccent,
  useThemeStore,
} from './lib/theme'
import { useUserStore } from './stores/userStore'
import { useActivityPing } from './hooks/useActivityPing'
import { RequireCosmic } from './components/auth/RequireCosmic'
import { RequireAuth } from './components/auth/RequireAuth'
import { AuthLoadingScreen } from './components/auth/AuthLoadingScreen'
import { ScrollReset } from './components/ScrollReset'
import { claimPendingReferral } from './lib/referral'

// Keep the two root-index pages in the initial bundle — one of them is
// rendered on every cold open. Everything else is lazy so the initial JS
// payload shrinks dramatically (Lighthouse flagged 530 KiB of unused JS at
// first paint). React Router will Suspend each lazy chunk on first visit.
import { HomePage } from './pages/HomePage'
import { LandingPage } from './pages/LandingPage'

// Wrap React.lazy() for components exported as named (not default) bindings
// — every page in this app uses `export function PageName()` rather than
// `export default`, so we adapt the dynamic-import promise.
function lazyNamed<T extends string>(
  loader: () => Promise<Record<T, React.ComponentType<unknown>>>,
  name: T,
) {
  return lazy(() => loader().then((m) => ({ default: m[name] })))
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SkyPage = lazyNamed(() => import('./pages/SkyPage'), 'SkyPage')
const DeckLibraryPage = lazyNamed(() => import('./pages/DeckLibraryPage'), 'DeckLibraryPage')
const CardsPage = lazyNamed(() => import('./pages/CardsPage'), 'CardsPage')
const LivePage = lazyNamed(() => import('./pages/LivePage'), 'LivePage')
const CommunityPage = lazyNamed(() => import('./pages/CommunityPage'), 'CommunityPage')
const ProfilePage = lazyNamed(() => import('./pages/ProfilePage'), 'ProfilePage')
const ReadingPage = lazyNamed(() => import('./pages/ReadingPage'), 'ReadingPage')
const ServicesPage = lazyNamed(() => import('./pages/ServicesPage'), 'ServicesPage')
const PractitionerPage = lazyNamed(() => import('./pages/PractitionerPage'), 'PractitionerPage')
const SignInPage = lazyNamed(() => import('./pages/SignInPage'), 'SignInPage')
const SignUpPage = lazyNamed(() => import('./pages/SignUpPage'), 'SignUpPage')
const OnboardingPage = lazyNamed(() => import('./pages/OnboardingPage'), 'OnboardingPage')
const MoonCalendarPage = lazyNamed(() => import('./pages/MoonCalendarPage'), 'MoonCalendarPage')
const MoonSignListPage = lazyNamed(() => import('./pages/MoonSignListPage'), 'MoonSignListPage')
const JournalPage = lazyNamed(() => import('./pages/JournalPage'), 'JournalPage')
const AdminPortal = lazyNamed(() => import('./pages/admin/AdminPortal'), 'AdminPortal')
const BetaFeedbackPage = lazyNamed(() => import('./pages/BetaFeedbackPage'), 'BetaFeedbackPage')
const TermsPage = lazyNamed(() => import('./pages/TermsPage'), 'TermsPage')
const InstallPage = lazyNamed(() => import('./pages/InstallPage'), 'InstallPage')
const SharedReadingPage = lazyNamed(() => import('./pages/SharedReadingPage'), 'SharedReadingPage')
const OracleReadingAppPage = lazyNamed(() => import('./pages/pillars/OracleReadingAppPage'), 'OracleReadingAppPage')
const AstrologicalWeatherPage = lazyNamed(() => import('./pages/pillars/AstrologicalWeatherPage'), 'AstrologicalWeatherPage')
const PullACardPage = lazyNamed(() => import('./pages/pillars/PullACardPage'), 'PullACardPage')
const OracleCardReadingRenoPage = lazyNamed(() => import('./pages/pillars/OracleCardReadingRenoPage'), 'OracleCardReadingRenoPage')
const FindYourSignsPage = lazyNamed(() => import('./pages/events/FindYourSignsPage'), 'FindYourSignsPage')

const PaywallPage = lazyNamed(() => import('./pages/PaywallPage'), 'PaywallPage')
const AppearancePage = lazyNamed(() => import('./pages/profile/AppearancePage'), 'AppearancePage')
const InvitePage = lazyNamed(() => import('./pages/InvitePage'), 'InvitePage')

// Marketing sister pages (handoff v2 unauth surfaces) — public, share
// MarketingLayout chrome. Real content lands in milestones 6 & 7.
const AboutPage = lazyNamed(() => import('./pages/AboutPage'), 'AboutPage')
const PricingPage = lazyNamed(() => import('./pages/PricingPage'), 'PricingPage')
const HowItWorksPage = lazyNamed(() => import('./pages/HowItWorksPage'), 'HowItWorksPage')
// /help branches on auth + the `help_center` flag inside HelpRoute (marketing
// HelpPage vs. the in-app Help center); both live in the HelpRoute chunk.
const HelpRoute = lazyNamed(() => import('./routes/HelpRoute'), 'HelpRoute')

/** Suspense fallback shown briefly while a lazy route chunk loads.
 *  Matches the cosmic-black backdrop + gold pulse dot used elsewhere
 *  (RootIndexAuthed AuthLoadingScreen + pre-paint inline CSS) so route
 *  transitions feel continuous. */
function RouteSuspenseFallback() {
  return (
    <div className="flex items-center justify-center min-h-[50vh] bg-cosmic-black">
      <div className="w-1.5 h-1.5 rounded-full bg-gold/70 animate-pulse" aria-label="Loading" role="status" />
    </div>
  )
}

const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

// Cache the most-recent known sign-in state in localStorage so a refresh can
// hold a neutral loading screen during the Clerk-init window for users who
// were signed in last session — otherwise they'd see the LandingPage (logged-
// out hero) flash before Clerk rehydrates and HomePage swaps in. Written and
// cleared by AuthManager once Clerk confirms state. Stored as '1'/absent
// rather than 'true'/'false' so a stale string can't accidentally pass a
// truthiness check.
const PREVIOUSLY_SIGNED_IN_KEY = 'cf:was-signed-in'

/**
 * Handles all auth side effects:
 * 1. Sets the auth token getter for API calls
 * 2. Fetches user profile from our DB when signed in
 * 3. Fetches feature flags
 * 4. Redirects to onboarding if needed
 */
function AuthManager() {
  const { getToken, isSignedIn, isLoaded } = useAuth()
  const fetchProfile = useUserStore((s) => s.fetchProfile)
  const clearProfile = useUserStore((s) => s.clear)
  const fetchFlags = useFlagStore((s) => s.fetchFlags)

  // Record a daily "active" event for engagement analytics once signed in
  // (idempotent server-side — one row per user per UTC day).
  useActivityPing(isLoaded && !!isSignedIn)

  // Wire auth token getter
  useEffect(() => {
    setAuthTokenGetter(() => getToken())
    return () => setAuthTokenGetter(() => Promise.resolve(null))
  }, [getToken])

  // When Clerk loads and user is signed in, fetch profile + flags. Also
  // mirror the auth state into localStorage so the next refresh's pre-paint
  // script in index.html can hold the loading overlay before React mounts.
  // (See `cf:was-signed-in` reader in index.html + RootIndexAuthed.)
  useEffect(() => {
    if (!isLoaded) return

    try {
      if (isSignedIn) {
        window.localStorage.setItem(PREVIOUSLY_SIGNED_IN_KEY, '1')
      } else {
        window.localStorage.removeItem(PREVIOUSLY_SIGNED_IN_KEY)
      }
    } catch {
      // private mode / quota — the flash-suppression is a nice-to-have, not
      // a correctness invariant; ignore failures.
    }

    // Reveal the underlying tree now that Clerk has confirmed state — the
    // pre-paint script in index.html added .app-auth-loading to <html>, which
    // hides #root with CSS until this runs.
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('app-auth-loading')
    }

    if (isSignedIn) {
      fetchProfile().then(() =>
        fetchFlags().then(() => {
          // Once-per-load, post-auth: attribute any referral stashed pre-auth on
          // the sign-up route. Gated on the `sharing` flag so the whole feature
          // is inert when off (no claim POST). Idempotent + silent thereafter.
          if (useFlagStore.getState().flags['sharing']) claimPendingReferral()
        }),
      )
    } else {
      clearProfile()
      fetchFlags()
    }
  }, [isSignedIn, isLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // NOTE: Onboarding redirect disabled to fix redirect loop.
  // Users can access /onboarding manually or via a prompt on the home page.

  return null
}

/** Loads feature flags for non-Clerk setups */
function FlagLoader() {
  const fetchFlags = useFlagStore((s) => s.fetchFlags)
  useEffect(() => { fetchFlags() }, [fetchFlags])
  return null
}

function DocumentTitle() {
  useDocumentTitle()
  return null
}

/**
 * Reconciles the applied theme with the `theme_picker` flag + server truth.
 * The index.html pre-paint script already restored `cf:theme` before first
 * paint (reading the flag mirror), so this is the authoritative correction and
 * the single owner of the effective-theme derivation (see deriveEffectiveTheme):
 *  - flag OFF → DEFAULT_THEME (midnight today) EXACTLY as before — preview /
 *    stored / tier are ignored, `cf:theme` is preserved for when the flag
 *    flips back on.
 *  - flag ON  → preview ?? (paid ? stored : DEFAULT). Free users never see a
 *    stored palette; a forced surface still wins.
 */
export function ThemeManager() {
  const loaded = useFlagStore((s) => s.loaded)
  const themeEnabled = useFlagStore((s) => !!s.flags['theme_picker'])
  const profile = useUserStore((s) => s.profile)
  const stored = useThemeStore((s) => s.stored)
  const storedAccentHue = useThemeStore((s) => s.storedAccentHue)
  const preview = useThemeStore((s) => s.preview)
  // Reactive mirror of the active forced surface. Not read for its value (we read
  // getActiveForcedTheme() synchronously below); subscribing to it makes this
  // reconcile RE-RUN when a forced surface (e.g. the admin portal forcing
  // midnight) mounts / unmounts, so the custom accent is suppressed on entry and
  // restored on exit for a signed-in paid user.
  const forcedTheme = useThemeStore((s) => s.forcedTheme)
  const syncFromProfile = useThemeStore((s) => s.syncFromProfile)

  // Server-wins reconcile: when a profile loads/changes, mirror its theme into
  // the store + cf:theme. DOM application is left to the effect below so the
  // flag-off / forced-surface rules still hold.
  //
  // Gated on flag-on: syncFromProfile writes cf:theme when the server theme
  // differs from the mirror, so running it with the picker OFF would produce a
  // localStorage side effect that batch-2 never had. Flag off must stay
  // byte-identical, so we skip the reconcile entirely (cf:theme is preserved
  // for when the flag flips back on).
  useEffect(() => {
    if (!loaded || !themeEnabled) return
    if (profile) syncFromProfile(profile)
  }, [loaded, themeEnabled, profile, syncFromProfile])

  useEffect(() => {
    if (!loaded) return
    // If a forced surface (useForcedTheme, e.g. a marketing / auth / pillar
    // page) is mounted, defer to its theme — this reconcile is a passive effect
    // and runs AFTER that surface's layout effect on the same flag-load commit,
    // so getActiveForcedTheme() is already populated. Without this the stored
    // theme would clobber forced Daylight whenever cf:theme is non-midnight.
    const forced = getActiveForcedTheme()
    // The user's real (non-forced) effective theme — what a forced surface
    // restores to on unmount, so `.theme` must hold this and NOT the forced id.
    const nonForced = deriveEffectiveTheme({
      flagEnabled: themeEnabled,
      forced: null,
      isPaid: isPaidProfile(profile),
      stored,
      preview,
    })
    // persist:false — this reconcile must not overwrite the user's saved
    // `cf:theme` (esp. when forcing midnight under a disabled flag). Deliberate
    // picks (setTheme) own the mirror write.
    applyTheme(forced ?? nonForced, false)
    useThemeStore.setState({ theme: nonForced })

    // Custom accent: recompute against the theme now on the page whenever the
    // effective theme, tier, forced surface, or stored hue changes. GATED on
    // flag-on — applyAccent writes localStorage, and a disabled flag must stay
    // byte-identical to batch-2/3 (no accent DOM or localStorage writes at all).
    if (themeEnabled) {
      const effectiveOnPage = forced ?? nonForced
      if (
        shouldApplyAccent({
          flagEnabled: true,
          isPaid: isPaidProfile(profile),
          forced,
          storedAccentHue,
        })
      ) {
        applyAccent(storedAccentHue, effectiveOnPage)
      } else {
        // Suppressed (free / tier-preview-free / forced surface / no hue): clear
        // the nine vars + mirror + cache so no stale accent lingers.
        applyAccent(null, effectiveOnPage)
      }
    } else {
      // Flag OFF: the pre-paint script applies cached accent vars whenever the
      // flag mirror reads '1'. If the server flag has since flipped off, that
      // stale accent would otherwise stay stuck on <html> all session (the
      // themeEnabled block never runs to clear it). Strip the nine vars —
      // DOM-only, no localStorage, so flag-off stays byte-identical (batch-2);
      // removeProperty is a no-op when the vars were never applied.
      clearAccentVarsFromDom()
    }
  }, [loaded, themeEnabled, profile, stored, preview, storedAccentHue, forcedTheme])
  return null
}

function RootIndex() {
  // When Clerk is not configured (prerender / no-auth builds), always show Landing.
  if (!CLERK_ENABLED) return <LandingPage />
  return <RootIndexAuthed />
}

function wasPreviouslySignedIn(): boolean {
  if (typeof window === 'undefined') return false
  try {
    // 1. Our own flag — written by AuthManager once Clerk confirms state.
    //    Misses on the *first* refresh after the flag-writing code lands
    //    (the user's previous session pre-dates our write).
    if (window.localStorage.getItem(PREVIOUSLY_SIGNED_IN_KEY) === '1') return true
    // 2. Fallback: Clerk maintains `__client_uat` as a non-HttpOnly cookie
    //    whenever an active session exists. Reading it lets us hold the
    //    loading screen even on the first refresh after deploy, before our
    //    own flag has been written. Anonymous visitors don't have this
    //    cookie, so the SEO prerender + cold-open path is unaffected.
    if (typeof document !== 'undefined' && /(^|; ?)__client_uat=/.test(document.cookie)) {
      return true
    }
    return false
  } catch {
    // Private mode / disabled storage — treat as first-time visitor.
    return false
  }
}

function RootIndexAuthed() {
  const { isSignedIn, isLoaded } = useAuth()
  if (!isLoaded) {
    // Returning signed-in user: hold on a neutral loading screen until Clerk
    // confirms the session. First-time visitors (and the prerender browser
    // which blocks Clerk scripts so isLoaded never flips) still see the
    // LandingPage, which is what the SEO crawl + cold-open should see.
    return wasPreviouslySignedIn() ? <AuthLoadingScreen /> : <LandingPage />
  }
  return isSignedIn ? <HomePage /> : <LandingPage />
}


export default function App() {
  return (
    <>
      {/* Sits above the router — position:fixed overlay surfaced whenever
          navigator.onLine flips false. No router context needed. */}
      <OfflineBanner />
      <BrowserRouter>
        <DocumentTitle />
        <ScrollReset />
        {CLERK_ENABLED ? <AuthManager /> : <FlagLoader />}
        <ThemeManager />
        {CLERK_ENABLED && <TermsAcceptanceModal />}
        <div className="flex flex-col h-dvh bg-cosmic-black w-full relative overflow-hidden">
          <ChromeProvider>
          <TopBar />
          <main id="app-main" className="flex-1 overflow-y-auto">
            <Suspense fallback={<RouteSuspenseFallback />}>
              <Routes>
                {/* Tab pages */}
                <Route path="/" element={<RootIndex />} />
                <Route path="/decks" element={<RequireAuth><DeckLibraryPage /></RequireAuth>} />
                <Route path="/find-direction" element={<RequireAuth><Navigate to="/decks" replace /></RequireAuth>} />
                <Route path="/find-clarity" element={<RequireAuth><CardsPage /></RequireAuth>} />
                <Route path="/find-events" element={<RequireAuth><LivePage /></RequireAuth>} />
                <Route path="/find-community" element={<RequireAuth><CommunityPage /></RequireAuth>} />
                <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
                {/* Appearance (theme picker) — gates itself on the theme_picker
                    flag (off → redirect to /profile). */}
                <Route path="/profile/appearance" element={<RequireAuth><AppearancePage /></RequireAuth>} />
                {/* Invite / referral screen — gates itself on the `sharing`
                    flag (off → redirect to /profile). Guarded: with `sharing`
                    ON it immediately fetches /referrals/me, which 401s for a
                    signed-out visitor and strands them in an error state with
                    no way to sign in. Not a marketing surface — absent from
                    sitemap.xml and PUBLIC_ROUTES, so the guard costs no SEO. */}
                <Route path="/invite" element={<RequireAuth><InvitePage /></RequireAuth>} />

                {/* Legacy route redirects (back-compat for bookmarks) */}
                <Route path="/sky" element={<RequireAuth><Navigate to="/decks" replace /></RequireAuth>} />
                <Route path="/cards" element={<Navigate to="/find-clarity" replace />} />
                <Route path="/live" element={<RequireAuth><Navigate to="/find-events" replace /></RequireAuth>} />
                <Route path="/community" element={<RequireAuth><Navigate to="/find-community" replace /></RequireAuth>} />

                {/* Sub-pages */}
                {/* Unguarded on purpose: PaywallPage has its own signed-out
                    branch (tailored headline + benefits + a "Sign in to keep
                    your access" CTA) for founders arriving from a win-back
                    email while signed out. A route-table guard here would
                    replace that pitch with a bare login form. */}
                <Route path="/upgrade" element={<PaywallPage />} />
                <Route path="/moon-calendar" element={<RequireAuth><MoonCalendarPage /></RequireAuth>} />
                <Route path="/moon-sign-list" element={<RequireAuth><MoonSignListPage /></RequireAuth>} />
                <Route
                  path="/journal"
                  element={
                    <RequireCosmic feature="journal">
                      <JournalPage />
                    </RequireCosmic>
                  }
                />
                {/* Admin v2 portal: full-screen control room, owns its own chrome,
                    admin-role-gated inside the component. Nested views at /admin/<view>.
                    (The legacy /admin-legacy surface was retired once the new portal
                    absorbed all of its tools.) */}
                <Route path="/admin/*" element={<AdminPortal />} />
                <Route path="/beta-feedback" element={<RequireAuth><BetaFeedbackPage /></RequireAuth>} />
                <Route path="/terms" element={<TermsPage />} />

                {/* Full-screen pages (no bottom nav) */}
                <Route path="/reading" element={<RequireAuth><ReadingPage /></RequireAuth>} />
                <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />
                <Route path="/install" element={<InstallPage />} />
                {/* Public read-only shared reading. Sandboxed: no chrome, no nav,
                    no store access. Must stay OUTSIDE RequireAuth — an
                    anonymous recipient is the primary audience. */}
                <Route path="/shared/:shortid" element={<SharedReadingPage />} />
                <Route path="/services" element={<ServicesPage />} />
                {/* /find-practitioners is the brand-voice alias surfaced in the
                    bottom nav; /services is the canonical SEO URL (already
                    prerendered). Both render the same component. */}
                <Route path="/find-practitioners" element={<ServicesPage />} />
                <Route path="/practitioners/:slug" element={<PractitionerPage />} />
                <Route path="/sign-in/*" element={<SignInPage />} />
                <Route path="/sign-up/*" element={<SignUpPage />} />

                {/* Marketing sister pages (handoff v2). Public, share
                    MarketingLayout chrome via the page components themselves. */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/help" element={<HelpRoute />} />

                {/* SEO pillar pages */}
                <Route path="/oracle-reading-app" element={<OracleReadingAppPage />} />
                <Route path="/astrological-weather" element={<AstrologicalWeatherPage />} />
                <Route path="/pull-a-card" element={<PullACardPage />} />
                <Route path="/oracle-card-reading-reno" element={<OracleCardReadingRenoPage />} />
                <Route path="/find-your-signs" element={<FindYourSignsPage />} />
                {/* Catch-all: an unknown route must never render a blank page */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </main>
          {/* Floating theme preview/upgrade bar — docks above the tab bar on
              every screen while a free viewer is previewing a paid palette.
              Renders null (zero layout change) otherwise, incl. flag-off. */}
          <ThemePreviewBar />
          <BottomNav />
          <UpdateToast />
          {/* App-wide help video player — opened via useHelpSheet from the Help
              center, the onboarding row, or contextual hint dots. Renders null
              (zero layout change) until something opens it, incl. flag-off.
              Mounted *after* UpdateToast on purpose: both the toast and the
              sheet's backdrop are z-50, so DOM order is the tiebreaker and the
              modal must paint over the toast, not under it. */}
          <HelpVideoSheet />
          </ChromeProvider>
        </div>
      </BrowserRouter>
    </>
  )
}
